import { UserHasRole } from './Roles';
import clientFirebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import { KatapultModel } from './katapult-model/KatapultModel.js';

/** @import { ClientOrAdminFirebase } from './types/ClientOrAdminFirebase.js' */

/**
 * Determines if a user can edit a model based on their roles and the model's editor settings. This rule should match the logic in the
 * database security rules for writing to models.
 * @param {string} userId - The user to check permissions for
 * @param {string} modelId - The company or model to check permissions against
 * @param {object} [options] - Additional options
 * @param {ClientOrAdminFirebase} [options.firebase] - The firebase instance to use
 * @param {string} [options.userGroup] - The user's active company (userGroup); must be provided if using the admin SDK
 * @param {string} [options.rootCompany] - The user's root company; must be provided if using the admin SDK
 * @returns {Promise<boolean>} - True if the user can edit the model, false otherwise
 */
export async function UserCanEditModel(userId, modelId, options) {
  const { firebase = clientFirebase, userGroup: _userGroup, rootCompany: _rootCompany } = options ?? {};

  // Always allow super admins to edit models
  const isSuperAdminRef = firebase.database().ref(`admin_accounts/${userId}`);
  const isSuperAdmin = (await isSuperAdminRef.once('value')).val();
  if (isSuperAdmin === true) return true;

  // Get the user's active company
  const userGroup = _userGroup || (await getUserGroupFromAuth(firebase));
  if (!userGroup) throw new Error('User group is required to determine model edit permissions');

  // TODO (2025-11-24): Remove this when we are done with root company permission checks.
  const rootCompany = _rootCompany || (await firebase.auth()['currentUser']?.getIdTokenResult())?.claims.rootCompany;

  // Check if the user has write access to the model
  const userHasWriteAccess = await userHasWriteAccessToModel(userId, userGroup, modelId, firebase, rootCompany);
  if (!userHasWriteAccess) return false;

  // Get the model options
  const modelOptionsRef = firebase.database().ref(`photoheight/company_space/${modelId}/models/config/model_editor`);
  const modelOptions = (await modelOptionsRef.once('value')).val() || {};

  // If admins only is false, any user with the model editor role can edit the model so return true
  const adminsOnly = modelOptions?.admins_only || false;
  if (!adminsOnly) return true;

  // Check to see if the user is an admin of their active company
  const isAdminRef = firebase.database().ref(`photoheight/company_space/${userGroup}/admins/${userId}`);
  const isAdmin = (await isAdminRef.once('value')).val() != null || false;
  if (isAdmin) return true;

  // If the user is listed as a model editor, they can edit the model
  const modelEditors = modelOptions?.editors || {};
  return modelEditors[userId] === true;
}

/**
 * @param {string} userId
 * @param {string} userGroup
 * @param {string} modelId
 * @param {ClientOrAdminFirebase} firebase
 * @param {string} rootCompany
 */
async function userHasWriteAccessToModel(userId, userGroup, modelId, firebase, rootCompany) {
  // User has model editor access for their active company
  const userHasModelEditorRole = await UserHasRole(userId, 'model_editor', userGroup, { firebase });
  if (userHasModelEditorRole) {
    // If the model is the user's active company, they can write to it
    if (modelId === userGroup) return true;

    // If the user has individual write access to the model, they can write to it
    const userHasWriteAccessToCompany = (
      await firebase.database().ref(`user_groups/${userId}/individual_permissions/${modelId}/write`).once('value')
    ).val();
    if (userHasWriteAccessToCompany) return true;

    // If the user's active company has write access to the model, they can write to it
    const writeAccessFlag = await KatapultModel.fromId(modelId, { firebaseApp: firebase }).getCompanyWriteAccess(userGroup);
    if (writeAccessFlag) return true;
  }

  // If none of the checks passed, the user does not have write access
  return false;
}

/**
 * @param {ClientOrAdminFirebase} firebase
 */
async function getUserGroupFromAuth(firebase) {
  const auth = firebase.auth();
  if ('currentUser' in auth) {
    const tokenResult = await auth.currentUser?.getIdTokenResult();
    const userGroupClaim = tokenResult?.claims.userGroup;
    return typeof userGroupClaim === 'string' ? userGroupClaim : null;
  }

  return null;
}
